import React, { useCallback, useMemo, useState, memo } from 'react';
import {
  Box,
  Grid2 as Grid,
  Badge,
  Checkbox,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
  Fab,
  Collapse,
  IconButton,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import type { ScreenSelectionProps, TargetGroup } from '@/entities/campaign';
import { RootState, useAppSelector, useAppDispatch } from '@/app/store';
import {
  getCityGroupById,
  getCityGroupFilters,
} from '@/shared/utils/cityGroups';
import FilterBar from '../ScreenSelection/FilterBar';
import ScreenCart from '../ScreenSelection/ScreenCart';
import StreetMap from '../ScreenSelection/Maps';
import { DEFAULT_SCREEN_IMAGE } from '@/shared/constants/advertiserCards';
import { useScreensQuery } from '@/features/dooh/screen/hooks/useScreenQueries';
import { handleSnackbar } from '@/shared/ui';
import { setAdvertiserBasic } from '../../campaignsSlice';
import { calculateAspectRatio } from '@/shared/utils';
import { useQueryErrorSnackbar } from '@/shared/hooks';

// ─── Slot Counter Pill ───────────────────────────────────────────────────
const SovCounter = memo(
  ({
    slots,
    canIncrement,
    canDecrement,
    onIncrement,
    onDecrement,
  }: {
    slots: number;
    canIncrement: boolean;
    canDecrement: boolean;
    onIncrement: (e: React.MouseEvent) => void;
    onDecrement: (e: React.MouseEvent) => void;
  }) => {
    const isActive = slots > 0;

    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          bgcolor: isActive ? '#43a047' : '#bdbdbd',
          borderRadius: '24px',
          px: 1,
          py: 0.5,
          minWidth: 96,
          justifyContent: 'space-between',
          transition: 'background-color 0.25s ease',
        }}
      >
        <IconButton
          size="small"
          onClick={onDecrement}
          disabled={!canDecrement}
          sx={{
            color: 'white',
            p: '3px',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
            '&:disabled': { color: 'rgba(255,255,255,0.3)' },
          }}
        >
          <RemoveIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Typography
          sx={{
            color: 'white',
            fontWeight: 700,
            fontSize: '0.8rem',
            minWidth: 20,
            textAlign: 'center',
            userSelect: 'none',
            lineHeight: 1,
          }}
        >
          {slots}
        </Typography>
        <IconButton
          size="small"
          onClick={onIncrement}
          disabled={!canIncrement}
          sx={{
            color: 'white',
            p: '3px',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
            '&:disabled': { color: 'rgba(255,255,255,0.3)' },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    );
  }
);
SovCounter.displayName = 'SovCounter';

// ─── Screen List Item with inline SOV ────────────────────────────────────
const OpsScreenListItem = memo(
  ({
    screen,
    consumedSlots,
    checked,
    highlightedScreenId,
    onScreenClick,
    onSovChange,
    onCheckToggle,
    isEditMode = false,
    effectiveMax,
  }: {
    screen: any;
    consumedSlots: number;
    checked: boolean;
    highlightedScreenId: string | null;
    onScreenClick: (screen: any) => void;
    onSovChange: (screen: any, newSlots: number) => void;
    onCheckToggle: (screenId: string) => void;
    isEditMode?: boolean;
    effectiveMax: number;
  }) => {
    const dispatch = useAppDispatch();
    const maxSlots = screen?.numberOfSlots || 0;
    const occupiedSlots = screen?.occupiedSlotCount || 0;
    const availableSlots = Math.max(0, maxSlots - occupiedSlots);
    const isDisabled = availableSlots === 0 && !(isEditMode && checked);
    const isActive = consumedSlots > 0;

    const handleScreenClick = useCallback(() => {
      if (isDisabled) {
        dispatch(
          handleSnackbar({
            open: true,
            message: 'No slot available in screen',
            severity: 'warning',
          })
        );
        return;
      }
      onScreenClick(screen);
    }, [onScreenClick, screen, isDisabled, dispatch]);

    const handleIncrement = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (consumedSlots < effectiveMax) {
          onSovChange(screen, consumedSlots + 1);
        }
      },
      [screen, consumedSlots, effectiveMax, onSovChange]
    );

    const handleDecrement = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (consumedSlots > 0) {
          onSovChange(screen, consumedSlots - 1);
        }
      },
      [screen, consumedSlots, onSovChange]
    );

    const handleCheckToggle = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onCheckToggle(screen.id);
      },
      [screen.id, onCheckToggle]
    );

    return (
      <ListItem
        key={screen?.id}
        id={`screen-item-${screen?.id}`}
        divider
        sx={{
          bgcolor:
            highlightedScreenId === screen?.id
              ? 'rgba(25, 118, 210, 0.1)'
              : isActive
                ? 'rgba(46, 125, 50, 0.05)'
                : 'transparent',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          transition: 'background-color 0.3s ease',
          opacity: isDisabled ? 0.5 : 1,
          borderLeft: isActive ? '3px solid #43a047' : '3px solid transparent',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: { xs: 1, sm: 0 },
        }}
        onClick={handleScreenClick}
      >
        <Checkbox
          checked={checked}
          onClick={handleCheckToggle}
          disabled={isDisabled}
          size="small"
          sx={{
            mr: 1,
            '&.Mui-checked': { color: 'primary.main' },
          }}
        />
        <ListItemText
          primary={screen?.name}
          secondary={
            <Box
              component="span"
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                rowGap: 0.25,
                columnGap: { xs: 0.75, md: 1.5 },
                mt: 0.5,
                fontSize: '0.8rem',
              }}
            >
              <Box component="span">Price: ₹{screen?.price ?? 0}/month</Box>
              <Box component="span">
                Available Slots: {availableSlots}/{maxSlots}
              </Box>
              {screen?.resolution && (
                <Box component="span">
                  Resolution: {screen.resolution.width || '?'}x
                  {screen.resolution.height || '?'}
                </Box>
              )}
              {screen?.resolution && (
                <Box component="span">
                  Aspect Ratio: {calculateAspectRatio(screen.resolution)}
                </Box>
              )}
            </Box>
          }
          primaryTypographyProps={{
            fontWeight: 600,
            color: isActive ? '#43a047' : 'text.primary',
          }}
        />
        <Box sx={{ flexShrink: 0, ml: { xs: 'auto', sm: 0 } }}>
          <SovCounter
            slots={consumedSlots}
            canIncrement={consumedSlots < effectiveMax}
            canDecrement={consumedSlots > 0}
            onIncrement={handleIncrement}
            onDecrement={handleDecrement}
          />
        </Box>
      </ListItem>
    );
  }
);
OpsScreenListItem.displayName = 'OpsScreenListItem';

// ─── Touchpoint-level slot control ───────────────────────────────────────
const TouchpointSovHeader = memo(
  ({
    groupName,
    screenCount,
    totalSlots,
    maxSlots,
    isExpanded,
    allChecked,
    someChecked,
    onToggleExpand,
    onTouchpointSovChange,
    onCheckAllToggle,
    selectedCount,
    isEditMode,
    hideExpand = false,
  }: {
    groupName: string;
    screenCount: number;
    totalSlots: number;
    maxSlots: number;
    isExpanded: boolean;
    allChecked: boolean;
    someChecked: boolean;
    onToggleExpand: () => void;
    onTouchpointSovChange: (delta: number) => void;
    onCheckAllToggle: (checked: boolean) => void;
    selectedCount?: number;
    isEditMode?: boolean;
    hideExpand?: boolean;
  }) => {
    const isActive = totalSlots > 0;

    const handleIncrement = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onTouchpointSovChange(1);
      },
      [onTouchpointSovChange]
    );

    const handleDecrement = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onTouchpointSovChange(-1);
      },
      [onTouchpointSovChange]
    );

    return (
      <Box
        onClick={onToggleExpand}
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          p: 1.5,
          borderRadius: 1,
          mb: isExpanded ? 1 : 0,
          cursor: hideExpand ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          '&:hover': { bgcolor: 'primary.dark' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Checkbox
            checked={allChecked}
            indeterminate={!allChecked && someChecked}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onCheckAllToggle(!allChecked);
            }}
            size="small"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              '&.Mui-checked': { color: 'white' },
              '&.MuiCheckbox-indeterminate': { color: 'white' },
              p: 0.5,
            }}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {groupName} ({screenCount})
            </Typography>
            {isEditMode && selectedCount !== undefined && selectedCount > 0 && (
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  display: 'block',
                  lineHeight: 1.2,
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                Selected screens: {selectedCount}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.25,
              bgcolor: isActive ? '#43a047' : 'rgba(0,0,0,0.15)',
              borderRadius: '24px',
              px: 1,
              py: 0.5,
              ml: 1,
              transition: 'background-color 0.25s ease',
              boxShadow: isActive
                ? '0 0 0 1px rgba(67,160,71,0.4)'
                : '0 0 0 1px rgba(255,255,255,0.15)',
            }}
          >
            <IconButton
              size="small"
              onClick={handleDecrement}
              disabled={totalSlots <= 0}
              sx={{
                color: 'white',
                p: '3px',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                '&:disabled': { color: 'rgba(255,255,255,0.3)' },
              }}
            >
              <RemoveIcon sx={{ fontSize: 15 }} />
            </IconButton>
            <Typography
              sx={{
                color: 'white',
                fontWeight: 700,
                fontSize: '0.8rem',
                minWidth: 20,
                textAlign: 'center',
                userSelect: 'none',
                lineHeight: 1,
              }}
            >
              {totalSlots}
            </Typography>
            <IconButton
              size="small"
              onClick={handleIncrement}
              disabled={totalSlots >= maxSlots}
              sx={{
                color: 'white',
                p: '3px',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                '&:disabled': { color: 'rgba(255,255,255,0.3)' },
              }}
            >
              <AddIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
          {!hideExpand && (
            <IconButton size="small" sx={{ color: 'primary.contrastText' }}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
      </Box>
    );
  }
);
TouchpointSovHeader.displayName = 'TouchpointSovHeader';

// ─── Shared slot array builder ────────────────────────────────────────────
// Grows or shrinks the slots array to targetCount, preserving existing slots
const buildSlots = (existingSlots: any[], targetCount: number): any[] => {
  if (targetCount > existingSlots.length) {
    return [
      ...existingSlots,
      ...Array.from({ length: targetCount - existingSlots.length }, (_, i) => ({
        name: `Slot ${existingSlots.length + i + 1}`,
        creatives: [],
      })),
    ];
  }
  return existingSlots.slice(0, targetCount);
};

const screenEntry = (
  existing: any,
  screen: any,
  slots: number,
  maxSlots: number
): any => ({
  ...(existing ?? screen),
  consumedSlots: slots,
  sov: Math.round((slots / maxSlots) * 100),
  slots: buildSlots(existing?.slots ?? [], slots),
  deviceImage: screen.deviceImage || screen.images || [],
  touchpoint: screen.touchpoint,
  geoCoordinates: screen.geoCoordinates,
});

const upsertScreen = (list: any[], screen: any, slots: number): any[] => {
  const maxSlots = screen.numberOfSlots || 0;
  if (slots === 0) return list.filter(s => s.id !== screen.id);
  const exists = list.some(s => s.id === screen.id);
  return exists
    ? list.map(s =>
        s.id === screen.id ? screenEntry(s, screen, slots, maxSlots) : s
      )
    : [...list, screenEntry(null, screen, slots, maxSlots)];
};

// ─── Main OpsScreenSelection Component ───────────────────────────────────
const OpsScreenSelection: React.FC<ScreenSelectionProps> = ({
  formik,
  source,
}) => {
  const isEditMode = formik.isEditMode ?? false;
  const [cartOpen, setCartOpen] = useState(false);
  const [highlightedScreenId, setHighlightedScreenId] = useState<string | null>(
    null
  );
  const [expandedTouchpoints, setExpandedTouchpoints] = useState<Set<string>>(
    new Set()
  );
  const [screenSizeRange, setScreenSizeRange] = useState<number[]>([]);
  const [screenType, setScreenType] = useState<string>('');
  const [zone, setZone] = useState<string>('');
  const [propertyType, setPropertyType] = useState<string>('');
  // Per-touchpoint slot level: tracks the uniform slot count applied via the header +/-
  const [touchpointSlotLevel, setTouchpointSlotLevel] = useState<
    Record<string, number>
  >({});
  // Slot level applied to all touchpoints via the "All Touchpoints" header +/-
  const [allTouchpointsSlotLevel, setAllTouchpointsSlotLevel] = useState(0);
  // Per-touchpoint screen search query
  const [screenSearch, setScreenSearch] = useState<string>('');
  // Checked screens: derived from selectedScreens in Redux
  const selectedScreenIds: string[] = useAppSelector((state: RootState) =>
    (state.dooh.campaigns.campaignDetails.selectedScreens || []).map(
      (s: any) => s.id
    )
  );
  const checkedScreens = useMemo(
    () => new Set<string>(selectedScreenIds),
    [selectedScreenIds]
  );
  const dispatch = useAppDispatch();

  const {
    currentTarget,
    targetGroups,
    selectedCityGroup,
    selectedTouchpoints,
    originalScreenSlots,
  } = useAppSelector(
    (state: RootState) => state.dooh.campaigns.campaignDetails
  );

  const { user } = useAppSelector((state: RootState) => state.auth);

  const { sidebarOpen } = useAppSelector((state: RootState) => state.ui);

  const queryParams = useMemo(() => {
    // Build city/state filters from all selected city groups
    const cityGroupFilters = Array.isArray(selectedCityGroup)
      ? selectedCityGroup.flatMap(id => getCityGroupFilters(id) ?? [])
      : selectedCityGroup
        ? (getCityGroupFilters(selectedCityGroup as any) ?? [])
        : [];

    const allStates = [
      ...new Set(
        cityGroupFilters
          .filter((f: any) => f.key === 'address.state')
          .flatMap((f: any) => f.value)
      ),
    ];
    const allCities = [
      ...new Set(
        cityGroupFilters
          .filter((f: any) => f.key === 'address.city')
          .flatMap((f: any) => f.value)
      ),
    ];

    return {
      filters: {
        ...(!isEditMode &&
          selectedTouchpoints &&
          selectedTouchpoints.length > 0 && {
            touchpoint: {
              key: 'touchpoint.id',
              type: 'terms',
              value: selectedTouchpoints,
            },
          }),
        ...(allStates.length > 0 && {
          addressState: {
            key: 'address.state',
            type: 'terms',
            value: allStates,
          },
        }),
        ...(allCities.length > 0 && {
          addressCity: {
            key: 'address.city',
            type: 'terms',
            value: allCities,
          },
        }),
        state: {
          key: 'state',
          type: 'terms',
          value: ['CONNECTED'],
        },
        ...(user?.partner_id && {
          partnerId: {
            key: 'partnerId',
            type: 'terms',
            value: [user.partner_id],
          },
        }),
      },
      search: '',
      page: 0,
      rowsPerPage: 1000,
    };
  }, [selectedCityGroup, selectedTouchpoints, user?.partner_id, isEditMode]);

  const {
    data,
    isLoading: screensLoading,
    error: screensError,
  } = useScreensQuery(queryParams);
  const screensData = data?.rows || [];

  useQueryErrorSnackbar(screensError, 'Failed to load screens');

  const totalScreensSelected = useMemo(() => {
    return (targetGroups || []).reduce(
      (total: number, tg: any) => total + (tg?.selectedArea?.length || 0),
      0
    );
  }, [targetGroups]);

  // In edit mode, slots held by this campaign count as available for it
  const baseScreens = useMemo(() => {
    const screens = Array.isArray(screensData) ? screensData : [];
    if (!isEditMode) return screens;
    return screens.map((s: any) => {
      const ownSlots = originalScreenSlots?.[s.id] || 0;
      return ownSlots > 0
        ? {
            ...s,
            occupiedSlotCount: Math.max(
              0,
              (s.occupiedSlotCount || 0) - ownSlots
            ),
          }
        : s;
    });
  }, [screensData, isEditMode, originalScreenSlots]);

  const screenSizeBounds = useMemo(() => {
    if (baseScreens.length === 0)
      return { min: 10, max: 800, availableSizes: [] as number[] };
    const sizes = baseScreens
      .map((s: any) => s.screenSize || 0)
      .filter((s: number) => s > 0);
    if (sizes.length === 0)
      return { min: 10, max: 800, availableSizes: [] as number[] };
    const unique = [...new Set<number>(sizes)].sort((a, b) => a - b);
    return {
      min: Math.min(...sizes),
      max: Math.max(...sizes),
      availableSizes: unique,
    };
  }, [baseScreens]);

  const availableScreenTypes = useMemo(() => {
    const types = new Set<string>();
    baseScreens.forEach((s: any) => {
      if (s.address?.type) types.add(s.address.type);
    });
    return Array.from(types).sort();
  }, [baseScreens]);

  const availableZones = useMemo(() => {
    const zones = new Set<string>();
    baseScreens.forEach((s: any) => {
      const z = s.touchpoint?.zone;
      if (z) zones.add(z);
    });
    return Array.from(zones).sort();
  }, [baseScreens]);

  const availablePropertyTypes = useMemo(() => {
    const pts = new Set<string>();
    baseScreens.forEach((s: any) => {
      const pt = s.touchpoint?.property_type;
      if (pt) pts.add(pt);
    });
    return Array.from(pts).sort();
  }, [baseScreens]);

  const availableScreens = useMemo(() => {
    let screens = baseScreens;
    if (!isEditMode && selectedTouchpoints?.length > 0) {
      screens = screens.filter(
        (s: any) =>
          s.touchpoint?.id && selectedTouchpoints.includes(s.touchpoint.id)
      );
    }
    if (screenSizeRange && screenSizeRange.length > 0) {
      screens = screens.filter((s: any) =>
        screenSizeRange.includes(s.screenSize || 0)
      );
    }
    if (screenType) {
      screens = screens.filter((s: any) => s.address?.type === screenType);
    }
    if (zone) {
      screens = screens.filter((s: any) => s.touchpoint?.zone === zone);
    }
    if (propertyType) {
      screens = screens.filter(
        (s: any) => s.touchpoint?.property_type === propertyType
      );
    }
    return screens;
  }, [
    baseScreens,
    selectedTouchpoints,
    screenSizeRange,
    screenType,
    zone,
    propertyType,
  ]);

  const mapItems = useMemo(() => {
    return availableScreens
      .map((screen: any) => {
        const lat =
          screen?.geoCoordinates?.lat ??
          screen?.geoCoordinates?.latitude ??
          screen?.lat;
        const lng =
          screen?.geoCoordinates?.lon ??
          screen?.geoCoordinates?.longitude ??
          screen?.lng;
        if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
          return {
            ...screen,
            coordinate: [lat, lng] as [number, number],
            selected: highlightedScreenId === screen?.id,
            images: screen?.images || [DEFAULT_SCREEN_IMAGE],
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [availableScreens, highlightedScreenId]);

  // Get consumed slots for a screen from the current target's selectedArea
  const getConsumedSlots = useCallback(
    (screenId: string): number => {
      const selected = currentTarget?.selectedArea?.find(
        (s: any) => s.id === screenId
      );
      return selected?.consumedSlots || 0;
    },
    [currentTarget]
  );

  const getMaxSlotsForScreens = useCallback(
    (screens: any[]): number =>
      screens.reduce((max: number, screen: any) => {
        const available = Math.max(
          0,
          (screen.numberOfSlots || 0) - (screen.occupiedSlotCount || 0)
        );
        return Math.max(max, Math.min(available, screen.numberOfSlots || 0));
      }, 0),
    []
  );

  const handleSovChange = useCallback(
    (screen: any, newSlots: number) => {
      if (!currentTarget) return;
      setHighlightedScreenId(screen.id);

      const maxSlots = screen.numberOfSlots || 0;
      const availableSlots = Math.max(
        0,
        maxSlots - (screen.occupiedSlotCount || 0)
      );
      const clampedSlots = Math.max(0, Math.min(availableSlots, newSlots));

      dispatch(
        setAdvertiserBasic({
          field: 'selectedScreens',
          value: upsertScreen(
            formik.values.selectedScreens || [],
            screen,
            clampedSlots
          ),
        })
      );

      const newSelectedArea = upsertScreen(
        currentTarget.selectedArea || [],
        screen,
        clampedSlots
      );
      const updatedTarget = { ...currentTarget, selectedArea: newSelectedArea };
      formik.setField('currentTarget', updatedTarget);
      formik.setField(
        'targetGroups',
        (targetGroups || []).map((tg: TargetGroup) =>
          tg.title === currentTarget.title ? updatedTarget : tg
        )
      );

      // If this screen's touchpoint has only 1 screen, sync the touchpoint slot level
      const tpId = screen.touchpoint?.id || screen.touchpointId;
      if (tpId) {
        const screensInTouchpoint = availableScreens.filter(
          (s: any) => (s.touchpoint?.id || s.touchpointId) === tpId
        );
        if (screensInTouchpoint.length === 1) {
          setTouchpointSlotLevel(prev => ({ ...prev, [tpId]: clampedSlots }));
        }
      }
    },
    [currentTarget, targetGroups, formik, dispatch, availableScreens]
  );

  // Highest consumed slots among the given screens (edit mode level display)
  const getHighestConsumedSlots = useCallback(
    (screens: any[]): number =>
      screens.reduce(
        (max: number, screen: any) =>
          Math.max(max, getConsumedSlots(screen.id)),
        0
      ),
    [getConsumedSlots]
  );

  // Applies a uniform slot level to the given screens in a single update
  const applySlotLevelToScreens = useCallback(
    (screens: any[], newLevel: number, delta: number) => {
      if (!currentTarget) return;

      let updatedSelectedArea = [...(currentTarget.selectedArea || [])];
      let updatedSelectedScreens = [...(formik.values.selectedScreens || [])];

      screens.forEach((screen: any) => {
        const maxSlots = screen.numberOfSlots || 0;
        const available = Math.max(
          0,
          maxSlots - (screen.occupiedSlotCount || 0)
        );
        if (available === 0) return;
        if (delta < 0 && !checkedScreens.has(screen.id)) return;

        const clamped = Math.max(0, Math.min(available, newLevel));

        updatedSelectedArea = upsertScreen(
          updatedSelectedArea,
          screen,
          clamped
        );
        updatedSelectedScreens = upsertScreen(
          updatedSelectedScreens,
          screen,
          clamped
        );
      });

      dispatch(
        setAdvertiserBasic({
          field: 'selectedScreens',
          value: updatedSelectedScreens,
        })
      );

      const updatedTarget = {
        ...currentTarget,
        selectedArea: updatedSelectedArea,
      };
      formik.setField('currentTarget', updatedTarget);
      formik.setField(
        'targetGroups',
        (targetGroups || []).map((tg: TargetGroup) =>
          tg.title === currentTarget.title ? updatedTarget : tg
        )
      );
    },
    [currentTarget, targetGroups, formik, checkedScreens, dispatch]
  );

  const handleTouchpointSovChange = useCallback(
    (touchpointId: string, screens: any[], delta: number) => {
      if (!currentTarget) return;

      const maxAvailableInTouchpoint = getMaxSlotsForScreens(screens);
      const currentLevel = isEditMode
        ? getHighestConsumedSlots(screens)
        : touchpointSlotLevel[touchpointId] || 0;

      const newLevel = Math.max(
        0,
        Math.min(maxAvailableInTouchpoint, currentLevel + delta)
      );
      setTouchpointSlotLevel(prev => ({ ...prev, [touchpointId]: newLevel }));
      applySlotLevelToScreens(screens, newLevel, delta);
    },
    [
      currentTarget,
      isEditMode,
      touchpointSlotLevel,
      getMaxSlotsForScreens,
      getHighestConsumedSlots,
      applySlotLevelToScreens,
    ]
  );

  const handleAllTouchpointsSovChange = useCallback(
    (delta: number) => {
      if (!currentTarget) return;

      const maxAvailable = getMaxSlotsForScreens(availableScreens);
      const currentLevel = isEditMode
        ? getHighestConsumedSlots(availableScreens)
        : allTouchpointsSlotLevel;

      const newLevel = Math.max(
        0,
        Math.min(maxAvailable, currentLevel + delta)
      );
      setAllTouchpointsSlotLevel(newLevel);

      const touchpointLevels: Record<string, number> = {};
      availableScreens.forEach((s: any) => {
        touchpointLevels[s.touchpoint?.id || s.touchpointId || 'unknown'] =
          newLevel;
      });
      setTouchpointSlotLevel(prev => ({ ...prev, ...touchpointLevels }));
      applySlotLevelToScreens(availableScreens, newLevel, delta);
    },
    [
      currentTarget,
      isEditMode,
      availableScreens,
      allTouchpointsSlotLevel,
      getMaxSlotsForScreens,
      getHighestConsumedSlots,
      applySlotLevelToScreens,
    ]
  );

  const handleScreenCheckToggle = useCallback(
    (screenId: string) => {
      const wasChecked = checkedScreens.has(screenId);
      const currentSelected = formik.values.selectedScreens || [];

      if (wasChecked) {
        // Uncheck: remove from selectedScreens and reset slots
        dispatch(
          setAdvertiserBasic({
            field: 'selectedScreens',
            value: currentSelected.filter((s: any) => s.id !== screenId),
          })
        );
        const screen = availableScreens.find((s: any) => s.id === screenId);
        if (screen) handleSovChange(screen, 0);
      } else {
        // Check: add to selectedScreens, auto-add 1 slot if needed
        const screen = availableScreens.find((s: any) => s.id === screenId);
        if (screen) {
          const consumed = getConsumedSlots(screenId);
          if (consumed === 0) handleSovChange(screen, 1);
          const slots = consumed > 0 ? consumed : 1;
          const maxSlots = screen.numberOfSlots || 1;
          const screenObj =
            currentTarget?.selectedArea?.find((s: any) => s.id === screenId) ??
            screenEntry(null, screen, slots, maxSlots);
          if (!currentSelected.some((s: any) => s.id === screenId)) {
            dispatch(
              setAdvertiserBasic({
                field: 'selectedScreens',
                value: [...currentSelected, screenObj],
              })
            );
          }
        }
      }
    },
    [
      checkedScreens,
      dispatch,
      getConsumedSlots,
      availableScreens,
      handleSovChange,
      formik,
      currentTarget,
    ]
  );

  // Toggle all screens in the given touchpoints
  const handleTouchpointCheckAllToggle = useCallback(
    (touchpointIds: string[], screens: any[], checked: boolean) => {
      let currentSelected = [...(formik.values.selectedScreens || [])];
      const screensNeedingSlot: any[] = [];
      const screensToReset: any[] = [];

      screens.forEach((s: any) => {
        const available = Math.max(
          0,
          (s.numberOfSlots || 0) - (s.occupiedSlotCount || 0)
        );
        if (available === 0) return;
        if (checked) {
          if (getConsumedSlots(s.id) === 0) screensNeedingSlot.push(s);
          if (!currentSelected.some((cs: any) => cs.id === s.id)) {
            currentSelected.push(screenEntry(null, s, 1, s.numberOfSlots || 1));
          }
        } else {
          if (getConsumedSlots(s.id) > 0) screensToReset.push(s);
          // Remove from selectedScreens
          currentSelected = currentSelected.filter((cs: any) => cs.id !== s.id);
        }
      });

      dispatch(
        setAdvertiserBasic({ field: 'selectedScreens', value: currentSelected })
      );

      const selectedCount = screens.filter((s: any) => {
        const available = Math.max(
          0,
          (s.numberOfSlots || 0) - (s.occupiedSlotCount || 0)
        );
        return available > 0;
      }).length;

      if (checked) {
        dispatch(
          handleSnackbar({
            open: true,
            message: `${selectedCount} screen${selectedCount !== 1 ? 's' : ''} selected`,
            severity: 'info',
            autoHideDuration: 3000,
          })
        );
      }

      setTouchpointSlotLevel(prev => {
        const next = { ...prev };
        touchpointIds.forEach(id => {
          next[id] = checked ? 1 : 0;
        });
        return next;
      });

      if (currentTarget) {
        let updatedSelectedArea = [...(currentTarget.selectedArea || [])];

        if (checked) {
          screensNeedingSlot.forEach((screen: any) => {
            const existing = updatedSelectedArea.find(
              (s: any) => s.id === screen.id
            );
            if (!existing) {
              updatedSelectedArea.push(
                screenEntry(null, screen, 1, screen.numberOfSlots || 1)
              );
            }
          });
        } else {
          const resetIds = new Set(screensToReset.map((s: any) => s.id));
          updatedSelectedArea = updatedSelectedArea.filter(
            (s: any) => !resetIds.has(s.id)
          );
        }

        const updatedTarget = {
          ...currentTarget,
          selectedArea: updatedSelectedArea,
        };
        const updatedGroups = (targetGroups || []).map((tg: TargetGroup) =>
          tg.title === currentTarget?.title ? updatedTarget : tg
        );
        formik.setField('currentTarget', updatedTarget);
        formik.setField('targetGroups', updatedGroups);
      }
    },
    [dispatch, getConsumedSlots, currentTarget, targetGroups, formik]
  );

  // Toggle all screens across all touchpoints
  const handleAllTouchpointsCheckToggle = useCallback(
    (checked: boolean) => {
      const touchpointIds = [
        ...new Set<string>(
          availableScreens.map(
            (s: any) => s.touchpoint?.id || s.touchpointId || 'unknown'
          )
        ),
      ];
      setAllTouchpointsSlotLevel(checked ? 1 : 0);
      handleTouchpointCheckAllToggle(touchpointIds, availableScreens, checked);
    },
    [availableScreens, handleTouchpointCheckAllToggle]
  );

  const handleScreenClick = useCallback((screen: any) => {
    setHighlightedScreenId(screen.id);
    const el = document.getElementById(`screen-item-${screen.id}`);
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const handleRemoveScreenFromCart = useCallback(
    (screenToRemove: any) => {
      const groups = formik.values.targetGroups || [];
      const updatedGroups = groups.map((tg: any) => {
        if (tg?.selectedArea?.some((s: any) => s.id === screenToRemove.id)) {
          return {
            ...tg,
            selectedArea: tg.selectedArea.filter(
              (s: any) => s.id !== screenToRemove.id
            ),
          };
        }
        return tg;
      });
      formik.setField('targetGroups', updatedGroups);
      if (
        currentTarget?.selectedArea?.some(
          (s: any) => s.id === screenToRemove.id
        )
      ) {
        formik.setField('currentTarget', {
          ...currentTarget,
          selectedArea: (currentTarget.selectedArea || []).filter(
            (s: any) => s.id !== screenToRemove.id
          ),
        });
      }

      const currentSelected = formik.values.selectedScreens || [];
      dispatch(
        setAdvertiserBasic({
          field: 'selectedScreens',
          value: currentSelected.filter((s: any) => s.id !== screenToRemove.id),
        })
      );
    },
    [formik, currentTarget, dispatch]
  );

  const handleUpdateTargetFilters = useCallback(
    (identifier: string, updates: Record<string, string>): TargetGroup[] => {
      return (targetGroups || []).map((tg: TargetGroup) => {
        if (tg.title !== identifier) return tg;
        const newFilters = { ...(tg.filters || {}) };
        Object.entries(updates).forEach(([key, value]) => {
          const isStateOrCity =
            key === 'address.state' || key === 'address.city';
          const isEmpty = !value;
          if (isEmpty && !isStateOrCity) delete newFilters[key];
          else if (!isEmpty) newFilters[key] = value;
        });
        return { ...tg, filters: newFilters };
      });
    },
    [targetGroups]
  );

  return (
    <Box width="80%" mx="auto">
      <FilterBar
        formik={formik}
        handleUpdateTargetFilters={handleUpdateTargetFilters}
        onAddTargetGroup={async () => targetGroups || []}
        source={source}
        screenSizeRange={screenSizeRange}
        setScreenSizeRange={setScreenSizeRange}
        screenType={screenType}
        setScreenType={setScreenType}
        screenSizeBounds={screenSizeBounds}
        availableScreenTypes={availableScreenTypes}
        zone={zone}
        setZone={setZone}
        propertyType={propertyType}
        setPropertyType={setPropertyType}
        availableZones={availableZones}
        availablePropertyTypes={availablePropertyTypes}
      />

      <Grid
        container
        spacing={2}
        sx={{
          width: '100%',
          boxShadow: theme => theme.shadows[2],
          borderRadius: 2,
          bgcolor: 'background.paper',
          p: 2,
        }}
      >
        {totalScreensSelected > 0 && (
          <Fab
            color="primary"
            aria-label="cart"
            sx={{
              position: 'fixed',
              bottom: { xs: 24, sm: 80 },
              left: { xs: 16, sm: 24, md: sidebarOpen ? '15rem' : 24 },
              zIndex: theme => theme.zIndex.drawer + 2,
              transition: theme =>
                theme.transitions.create(['left'], { duration: 300 }),
            }}
            onClick={() => setCartOpen(true)}
          >
            <Badge
              badgeContent={totalScreensSelected}
              color="error"
              overlap="circular"
              sx={{ '& .MuiBadge-badge': { top: '-13%', left: '60%' } }}
            >
              <ShoppingCartIcon />
            </Badge>
          </Fab>
        )}

        <ScreenCart
          cartOpen={cartOpen}
          setCartOpen={setCartOpen}
          formik={formik}
          handleToggleScreen={(screen: any) =>
            handleSovChange(screen, getConsumedSlots(screen.id) > 0 ? 0 : 1)
          }
          handleRemoveScreen={handleRemoveScreenFromCart}
        />

        {/* Screen Grouping by Touchpoints with inline SOV */}
        <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', minHeight: 0 }}>
          <Paper
            sx={{
              p: 2,
              height: { xs: 360, sm: 420, md: 500 },
              overflowY: 'auto',
              width: '100%',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Screen Grouping
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use +/- to allocate slots per screen
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Search screens..."
              value={screenSearch}
              onChange={e => setScreenSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 18, color: 'text.secondary' }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': { height: 36 },
              }}
            />

            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {screensLoading ? (
                <Stack spacing={1}>
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <Skeleton key={idx} variant="rectangular" height={40} />
                  ))}
                </Stack>
              ) : availableScreens.length > 0 ? (
                <Stack spacing={2}>
                  {(() => {
                    const selectableScreens = availableScreens.filter(
                      (s: any) =>
                        Math.max(
                          0,
                          (s.numberOfSlots || 0) - (s.occupiedSlotCount || 0)
                        ) > 0
                    );

                    return (
                      <TouchpointSovHeader
                        groupName="All Touchpoints"
                        screenCount={availableScreens.length}
                        totalSlots={
                          isEditMode
                            ? getHighestConsumedSlots(availableScreens)
                            : allTouchpointsSlotLevel
                        }
                        maxSlots={getMaxSlotsForScreens(availableScreens)}
                        isExpanded={false}
                        hideExpand
                        isEditMode={isEditMode}
                        selectedCount={
                          (formik.values.selectedScreens || []).length
                        }
                        allChecked={
                          selectableScreens.length > 0 &&
                          selectableScreens.every((s: any) =>
                            checkedScreens.has(s.id)
                          )
                        }
                        someChecked={availableScreens.some((s: any) =>
                          checkedScreens.has(s.id)
                        )}
                        onToggleExpand={() => {}}
                        onTouchpointSovChange={handleAllTouchpointsSovChange}
                        onCheckAllToggle={handleAllTouchpointsCheckToggle}
                      />
                    );
                  })()}
                  {(() => {
                    const touchpointGroups = new Map<
                      string,
                      { id: string; name: string; screens: any[] }
                    >();
                    availableScreens.forEach((screen: any) => {
                      const tpId =
                        screen.touchpoint?.id ||
                        screen.touchpointId ||
                        'unknown';
                      const tpName =
                        screen.touchpoint?.name ||
                        screen.touchpointName ||
                        'Unknown Location';
                      if (!touchpointGroups.has(tpId)) {
                        touchpointGroups.set(tpId, {
                          id: tpId,
                          name: tpName,
                          screens: [],
                        });
                      }
                      touchpointGroups.get(tpId)!.screens.push(screen);
                    });

                    return Array.from(touchpointGroups.values()).map(group => {
                      const isExpanded = expandedTouchpoints.has(group.id);
                      const toggleExpand = () => {
                        setExpandedTouchpoints(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(group.id)) newSet.delete(group.id);
                          else newSet.add(group.id);
                          return newSet;
                        });
                      };

                      // Current slot level for this touchpoint
                      const slotLevel = isEditMode
                        ? getHighestConsumedSlots(group.screens)
                        : touchpointSlotLevel[group.id] || 0;

                      const maxAvailableInGroup = getMaxSlotsForScreens(
                        group.screens
                      );

                      return (
                        <Box key={group.id}>
                          <TouchpointSovHeader
                            groupName={group.name}
                            screenCount={group.screens.length}
                            totalSlots={slotLevel}
                            maxSlots={maxAvailableInGroup}
                            isExpanded={isExpanded}
                            isEditMode={isEditMode}
                            selectedCount={
                              (formik.values.selectedScreens || []).filter(
                                (s: any) =>
                                  (s.touchpointId ?? s.touchpoint?.id) ===
                                  group.id
                              ).length
                            }
                            allChecked={
                              group.screens.filter(
                                (s: any) =>
                                  Math.max(
                                    0,
                                    (s.numberOfSlots || 0) -
                                      (s.occupiedSlotCount || 0)
                                  ) > 0
                              ).length > 0 &&
                              group.screens
                                .filter(
                                  (s: any) =>
                                    Math.max(
                                      0,
                                      (s.numberOfSlots || 0) -
                                        (s.occupiedSlotCount || 0)
                                    ) > 0
                                )
                                .every((s: any) => checkedScreens.has(s.id))
                            }
                            someChecked={group.screens.some((s: any) =>
                              checkedScreens.has(s.id)
                            )}
                            onToggleExpand={toggleExpand}
                            onTouchpointSovChange={delta =>
                              handleTouchpointSovChange(
                                group.id,
                                group.screens,
                                delta
                              )
                            }
                            onCheckAllToggle={checked =>
                              handleTouchpointCheckAllToggle(
                                [group.id],
                                group.screens,
                                checked
                              )
                            }
                          />
                          <Collapse
                            in={isExpanded}
                            timeout="auto"
                            unmountOnExit
                          >
                            <List disablePadding sx={{ pl: 2, mb: 2 }}>
                              {(() => {
                                const query = screenSearch.toLowerCase();
                                const filtered = group.screens.filter(
                                  (screen: any) => {
                                    if (!query) return true;
                                    return (screen?.name || '')
                                      .toLowerCase()
                                      .includes(query);
                                  }
                                );

                                if (filtered.length === 0) {
                                  return (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ py: 3, textAlign: 'center' }}
                                    >
                                      No screens found for "{screenSearch}"
                                    </Typography>
                                  );
                                }

                                return filtered.map((screen: any) => {
                                  const effectiveMax = Math.max(
                                    0,
                                    (screen.numberOfSlots || 0) -
                                      (screen.occupiedSlotCount || 0)
                                  );
                                  return (
                                    <OpsScreenListItem
                                      key={screen?.id}
                                      screen={screen}
                                      consumedSlots={getConsumedSlots(
                                        screen.id
                                      )}
                                      checked={checkedScreens.has(screen.id)}
                                      highlightedScreenId={highlightedScreenId}
                                      onScreenClick={handleScreenClick}
                                      onSovChange={handleSovChange}
                                      onCheckToggle={handleScreenCheckToggle}
                                      isEditMode={isEditMode}
                                      effectiveMax={effectiveMax}
                                    />
                                  );
                                });
                              })()}
                            </List>
                          </Collapse>
                        </Box>
                      );
                    });
                  })()}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No screens available for the selected filters.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Map */}
        <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', minHeight: 0 }}>
          <Paper
            sx={{
              p: 2,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Typography variant="h6" gutterBottom>
              {Array.isArray(selectedCityGroup) && selectedCityGroup.length > 0
                ? `Map View (${
                    selectedCityGroup
                      .map(id => getCityGroupById(id)?.displayName)
                      .filter(Boolean)
                      .join(', ') || 'Delhi-NCR'
                  })`
                : `Map View (${currentTarget?.state || ''} - ${currentTarget?.city || ''})`}
            </Typography>
            <Box
              sx={{
                flex: 1,
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
                minHeight: '300px',
                height: '100%',
              }}
            >
              {screensLoading ? (
                <Skeleton variant="rectangular" width="100%" height="100%" />
              ) : (
                <StreetMap items={mapItems} />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default memo(OpsScreenSelection);
